import type { CollectionSummaryJSONObject } from '@ikegps/core-api';
import { action, computed, observable, makeObservable } from 'mobx';
import { AutoCompleteMenu, type SelectAutoCompleteOption } from '../../components/autocomplete.menu/autocomplete.menu';
import { Input } from '../../components/input/input';
import { InputState } from '../../components/input/input.state';
import type { MenuItem } from '../../components/menu/menu.item';
import { ModalFormComponent, type ModalFormComponentCtx } from '../../components/modal.form/modal.form.component';
import { addStyles } from '../../components/theme';
import { T, Translate } from '../../i18n';
import { m, type MithrilVNode } from '../../mithril';
import { Services } from '../../services';
import { AppStore } from '../../store';
import { JobModalCard } from '../job.assign.modal/job.card';
import { CollectionMoveModalCtrl } from './collection.move.modal.ctrl';
import { CSS, Style } from './collection.move.modal.style';

addStyles(Style);

export class CollectionMoveToJobModal extends ModalFormComponent {
	private collections: CollectionSummaryJSONObject[];

	private ctrl: CollectionMoveModalCtrl;
	private jobInputState: InputState;

	@observable private selectedJob: string | null = null;
	private autoMenu: AutoCompleteMenu;

	constructor(ctx: ModalFormComponentCtx, collections: CollectionSummaryJSONObject[]) {
		super(ctx);
		makeObservable(this);

		this.ctrl = new CollectionMoveModalCtrl(collections);
		this.collections = collections;

		this.jobInputState = new InputState('Job');
		this.addState(this.jobInputState);

		this.autoMenu = new AutoCompleteMenu({
			inputState: this.jobInputState,
			options: () => this.jobOptions,
			optionView: (option: SelectAutoCompleteOption, nameParts: MithrilVNode[]) => this.viewJobCard(option, nameParts),
			onselect: (option) => this.selectJob(option),
		});
	}

	@computed get jobOptions(): SelectAutoCompleteOption[] {
		const rawJobs = AppStore.Tag.all;
		const jobs = rawJobs
			.filter((job) => Boolean(AppStore.TagSummary.get(job.objectId)))
			.map((tagObject) => ({ id: tagObject.objectId, value: tagObject.name }));
		return jobs;
	}

	get nodes(): MithrilVNode {
		return m('div', { className: CSS.container }, [
			m('div', { className: CSS.jobContent }, [
				m('p', Translate(T.COLLECTION_MOVE_CONFIRM, { count: this.ctrl.collections.length })),
				this.jobSelector(),
			]),
			m('div', { className: CSS.menu }, this.autoMenu.view()),
		]);
	}

	private viewJobCard(option: SelectAutoCompleteOption, nameParts: MithrilVNode[]): MenuItem {
		return {
			view: JobModalCard.viewCard({ tag: AppStore.Tag.get(option.id) }, nameParts),
			action: () => this.selectJob(option),
			testId: `move-job-option-${option.id}`,
		};
	}

	private jobSelector(): MithrilVNode {
		if (!this.selectedJob) {
			return Input.view(this.jobInputState);
		}

		return JobModalCard.viewChip({ tag: AppStore.Tag.get(this.selectedJob), cancel: () => this.selectJob() });
	}

	override validate() {
		if (!this.jobInputState._value && !this.selectedJob) {
			this.jobInputState.setError(Translate(T.COLLECTION_MOVE_JOB_INPUT_ERROR));
		} else {
			this.jobInputState.setError('');
		}
	}

	override async submit() {
		const tagName = this.selectedJob != null ? AppStore.Tag.get(this.selectedJob)?.name : this.jobInputState.value;

		await Services.Collection.move(this.collections, tagName);
	}

	@action private selectJob(option?: SelectAutoCompleteOption) {
		if (option != null) {
			this.selectedJob = option.id;
		} else {
			this.selectedJob = null;
			this.jobInputState.setValue('');
		}
	}
}
